import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CaretRightOutlined, SearchOutlined } from '@ant-design/icons'
import { Checkbox, Collapse, Input } from 'antd'
import { inject, observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import debounce from 'lodash.debounce'
import { blue } from '../../../../color'
import { AvatarHasName } from '../../../../components/Common/Avatar'
import { EmptyText } from '../../../../components/Common/CellText'
import { ASSIGNEE_TYPE } from '../../../../constants'
import { useUpdatingRiskExecutor } from '../../../RiskDetailPage/hooks/useUpdatingRiskExecutor'
import { BoxWrapper, PanelItem } from './SelectGroupBoxStyled'
import utils from '../../../../utils'

const SelectGroupBox = props => {
  const {
    selectPeopleStore,
    disableGroupList,
    loadingAnimationStore,
    onlySelectOne,
    onlySelectUser,
    riskStore,
    riskUnitUserStore,
    departmentStore,
    userStore,
  } = props

  const {
    groupList,
    setGroupList,
    originGroupList,
    assigneePopupType,
    selectGroupData,
    setSelectGroupData,
    selectUserData,
    setSelectUserData,
  } = selectPeopleStore

  const { riskId } = useParams()
  const {
    updateTier1RiskExecutor,
    updateTier2RiskExecutor,
    updateTier3RiskExecutor,
  } = useUpdatingRiskExecutor({
    riskStore,
    riskUnitUserStore,
    loadingAnimationStore,
    type: 'CQVP',
    riskId,
  })
  const { departmentList } = departmentStore
  const { userWithDepartment } = userStore

  // ✅ Thêm state để quản lý activeKey
  const [activeKeys, setActiveKeys] = useState([])

  // Ref lưu lại các group id đã fetch để không gọi lại API cho cùng 1 group
  const fetchedGroupIdsRef = useRef(new Set())
  // Ref lưu lại tập hợp group id trước đó nhằm kiểm tra xem danh sách group có thay đổi không
  const prevGroupIdsRef = useRef(new Set())

  // Tạo tập hợp group id hiện tại từ originGroupList
  const currentGroupIds = useMemo(
    () => new Set(originGroupList.map(group => group.id)),
    [originGroupList]
  )

  /**
   * Sử dụng debounce cho việc gọi API khi danh sách group thay đổi.
   * Khi danh sách group thay đổi, ta đợi 500ms (debounceDelay) trước khi gọi API,
   * giúp tránh việc gọi API liên tục khi dữ liệu thay đổi quá nhanh.
   */
  useEffect(() => {
    // Nếu danh sách group chưa thay đổi, không cần gọi API lại
    if (
      prevGroupIdsRef.current.size === currentGroupIds.size &&
      [...prevGroupIdsRef.current].every(id => currentGroupIds.has(id))
    ) {
      return
    }

    // Cập nhật lại tập hợp group đã có
    prevGroupIdsRef.current = currentGroupIds

    // Lọc ra những group id mới (chưa được fetch)
    const newGroupIds = [...currentGroupIds].filter(
      id => !fetchedGroupIdsRef.current.has(id)
    )
    if (newGroupIds.length === 0) return

    // Đánh dấu những group mới này là đã fetch để không gọi lại lần sau
    newGroupIds.forEach(id => fetchedGroupIdsRef.current.add(id))

    let isCancelled = false
    const debounceDelay = 500 // thời gian delay 500ms

    const handler = setTimeout(() => {
      const fetchData = async () => {
        try {
          // Nếu cần cập nhật executor, chỉ gọi một lần
          await updateTier3RiskExecutor
          const results = await Promise.allSettled([
            departmentStore.getDepartmentList(),
            userStore.getUserListV2(),
            ...newGroupIds.map(id => selectPeopleStore.getMemberGroup(id)),
          ])
          if (isCancelled) return
          console.log('Results:', results)
        } catch (error) {
          if (isCancelled) return
          console.error('Error fetching data:', error)
        }
      }
      fetchData()
    }, debounceDelay)

    return () => {
      isCancelled = true
      clearTimeout(handler)
    }
  }, [
    currentGroupIds,
    updateTier3RiskExecutor,
    departmentStore,
    userStore,
    selectPeopleStore,
  ])

  function convertCodeOrCodesToDepartName(input) {
    const codes = input?.split(',').map(code => code.trim())
    const names = codes?.map(code => {
      const role = departmentList?.find(role => role.code === code)
      return role ? role?.name : 'Không tìm thấy phòng ban'
    })
    return names?.join(', ')
  }

  function convertCodeOrCodesToNameFromDepartUser(currentUser) {
    const input = userWithDepartment?.find(
      user => user.username === currentUser
    )?.department
    const codes = input?.split(',').map(code => code.trim())
    const names = codes?.map(code => {
      const role = departmentList?.find(role => role.code === code)
      return role ? role?.name : 'Không tìm thấy phòng ban'
    })
    return names?.join(', ')
  }

  const handleSelectGroup = useCallback(
    (group, isChecked, event) => {
      event?.stopPropagation()
      if (!assigneePopupType || !group) return

      const newSelectGroupData = { ...selectGroupData }
      const newSelectUserData = { ...selectUserData }

      // Đảm bảo mảng tồn tại cho assigneePopupType
      if (!newSelectGroupData[assigneePopupType]) {
        newSelectGroupData[assigneePopupType] = []
      }
      if (!newSelectUserData[assigneePopupType]) {
        newSelectUserData[assigneePopupType] = []
      }

      if (isChecked) {
        // Thêm group mới nếu chưa tồn tại
        const existingGroupIndex = newSelectGroupData[
          assigneePopupType
        ].findIndex(g => g.id === group.id.toString())
        if (existingGroupIndex === -1) {
          newSelectGroupData[assigneePopupType].push({
            assignee_type: ASSIGNEE_TYPE.GROUP,
            full_name: group.name,
            id: group.id.toString(),
            permission: assigneePopupType,
          })
        }
      } else {
        // Nếu untick group, xóa group và các user thuộc group đó
        newSelectGroupData[assigneePopupType] = newSelectGroupData[
          assigneePopupType
        ].filter(g => g.id !== group.id.toString())

        const groupUserIds = group.users.map(u => u.id.toString())
        newSelectUserData[assigneePopupType] = newSelectUserData[
          assigneePopupType
        ].filter(user => !groupUserIds.includes(user.id))
      }

      // Cập nhật state
      setSelectGroupData(newSelectGroupData)
      setSelectUserData(newSelectUserData)
    },
    [assigneePopupType, selectGroupData, selectUserData, onlySelectOne]
  )

  const handleSelectUser = useCallback(
    (user, group, isChecked, event) => {
      event?.stopPropagation()
      if (!assigneePopupType || !user) return

      const newSelectUserData = { ...selectUserData }
      if (!newSelectUserData[assigneePopupType]) {
        newSelectUserData[assigneePopupType] = []
      }

      if (isChecked) {
        if (onlySelectOne) {
          newSelectUserData[assigneePopupType] = [
            {
              id: user.user.user_name.toString(),
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: user.user.name_lowercase,
              image_id: user.image,
              permission: assigneePopupType,
            },
          ]
        } else {
          const existingUserIndex = newSelectUserData[
            assigneePopupType
          ].findIndex(u => u.id === user.user.user_name.toString())
          if (existingUserIndex === -1) {
            newSelectUserData[assigneePopupType].push({
              id: user.user.user_name.toString(),
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: user.user.name_lowercase,
              image_id: user.image,
              permission: assigneePopupType,
            })
          }
        }
      } else {
        newSelectUserData[assigneePopupType] = newSelectUserData[
          assigneePopupType
        ].filter(item => item.id !== user.user.user_name.toString())
      }

      setSelectUserData(newSelectUserData)
    },
    [assigneePopupType, selectUserData, onlySelectOne]
  )

  // Sử dụng debounce cho hàm tìm kiếm nhóm
  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (value.trim() === '') {
          setGroupList(originGroupList)
        } else {
          const listGroupFilter = originGroupList.filter(group =>
            utils
              .nonAccentVietnamese(group.name)
              .includes(utils.nonAccentVietnamese(value))
          )
          setGroupList(listGroupFilter)
        }
      }, 300),
    [originGroupList, setGroupList]
  )

  const handleSearchGroup = useCallback(
    event => {
      const value = event.target.value
      setValueSearch(value)
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const [valueSearch, setValueSearch] = useState('')

  console.log(JSON.stringify(groupList))

  return (
    <BoxWrapper>
      <div className="search-box">
        <label style={{ fontWeight: 500, marginRight: 10 }}>
          <SearchOutlined style={{ marginRight: 4, color: blue }} />
          Tìm kiếm:
        </label>
        <Input
          placeholder="Nhập tên nhóm..."
          style={{ width: 300 }}
          onChange={handleSearchGroup}
          value={valueSearch}
        />
      </div>
      <div className="collapse-wrapper">
        <Collapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          collapsible="icon"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}>
          {groupList.map(group => {
            const isGroupChecked =
              assigneePopupType &&
              selectGroupData[assigneePopupType]?.some(
                el => el.id === group.id.toString()
              )
            const disabled =
              disableGroupList.includes(group.id) ||
              disableGroupList.includes(group.id.toString())
            return (
              <PanelItem
                key={group.id}
                header={
                  <div
                    className={`group-item ${
                      isGroupChecked ? 'group-item-checked' : ''
                    } ${disabled ? 'group-item-disabled' : ''}`}>
                    {!onlySelectOne && !onlySelectUser && (
                      <Checkbox
                        checked={isGroupChecked}
                        disabled={disabled}
                        onChange={e =>
                          handleSelectGroup(group, e.target.checked, e)
                        }
                      />
                    )}
                    <span className="group-name">{group.name}</span>
                  </div>
                }>
                {group.users.length > 0 ? (
                  group.users.map(user => {
                    const isUserChecked =
                      assigneePopupType &&
                      (isGroupChecked ||
                        selectUserData[assigneePopupType]?.some(
                          u => u.id === user.user.user_name.toString()
                        ))
                    return (
                      <div
                        className="user-item"
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}>
                        <Checkbox
                          checked={isUserChecked}
                          disabled={isGroupChecked || disabled}
                          onChange={e =>
                            handleSelectUser(user, group, e.target.checked, e)
                          }
                        />
                        <AvatarHasName
                          name={user.user.name_lowercase}
                          position={
                            user.user.department
                              ? convertCodeOrCodesToDepartName(
                                  user.user.department
                                )
                              : convertCodeOrCodesToNameFromDepartUser(
                                  user.user.user_name
                                )
                          }
                          imgId={user.image_id}
                        />
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '10px 0 10px 60px' }}>
                    <EmptyText>Không có người dùng</EmptyText>
                  </div>
                )}
              </PanelItem>
            )
          })}
        </Collapse>
      </div>
    </BoxWrapper>
  )
}

export default memo(
  inject(
    'userStore',
    'loadingAnimationStore',
    'selectPeopleStore',
    'riskStore',
    'riskUnitUserStore',
    'departmentStore'
  )(observer(SelectGroupBox))
)
